import { pickT } from "@repo/shared";
import type {
  MemberLinkInfoResponse,
  PublicLinkUnavailableReason,
  Translated,
} from "@repo/shared/types";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { CodeEntryForm } from "@/components/public-link-auth/code-entry-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError } from "@/lib/api/_client";
import {
  getMemberLinkInfo,
  sendMemberCode,
  verifyMemberCode,
} from "@/lib/api/public-flow/member-link";
import { useUserLanguage } from "@/lib/i18n/context";
import {
  MEMBER_SESSION_TOKEN_FOR_KEY,
  MEMBER_SESSION_TOKEN_KEY,
} from "@/pages/p/member-session-storage";
import { T } from "./page.i18n";

/** Maps `PublicLinkUnavailableReason` to the localized message. Unknown/absent falls back to `linkNotFound`. */
function unavailableReasonText(
  t: Translated<typeof T>,
  reason: PublicLinkUnavailableReason | null,
): string {
  switch (reason) {
    case "expired":
      return t.linkExpired;
    case "revoked":
      return t.linkRevoked;
    case "representative_removed":
      return t.representativeRemoved;
    case "representative_changed":
      return t.representativeChanged;
    case "member_removed":
      return t.memberRemoved;
    default:
      return t.linkNotFound;
  }
}

/**
 * QL-570 — the API picks the Idura environment from the tenant's mode and
 * redirects to its authorize URL. The eID always requests the `ssn` scope so
 * the callback can compare the signer's national id against the expected
 * signer's (QL-505) — see `criipto-auth-service.ts`.
 */
function startCriiptoMemberLogin(memberToken: string): void {
  const startUrl = new URL(`${process.env.BUN_PUBLIC_API_URL}/public/criipto/auth/start`);
  startUrl.searchParams.set("memberToken", memberToken);
  window.location.href = startUrl.toString();
}

const NETWORK_ERROR_INFO: MemberLinkInfoResponse = {
  valid: false,
  memberName: null,
  subjectKind: null,
  emailHint: null,
  phoneHint: null,
  bankIdAvailable: false,
  unavailableReason: "not_found",
  representativeName: null,
  // No response, so no flow language: English (QL-17 issue 027, out of scope).
  language: "en_GB",
};

export default function MemberLinkPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const memberToken = params.token as string;
  const errorCode = searchParams.get("error");

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<MemberLinkInfoResponse | null>(null);
  // QL-17 issue 027 — the info response carries the flow's language (a dead
  // link's too); until it arrives, the viewer's own.
  const { userLanguage } = useUserLanguage();
  const t = pickT(T, info?.language ?? userLanguage);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [lastSentMethod, setLastSentMethod] = useState<"email" | "sms" | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    let cancelled = false;
    getMemberLinkInfo(memberToken)
      .then((result) => {
        if (!cancelled) setInfo(result);
      })
      .catch(() => {
        // A network failure looks the same as a stale/typo'd link to the person.
        if (!cancelled) setInfo(NETWORK_ERROR_INFO);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberToken]);

  const sendCode = async (channel: "email" | "sms") => {
    setLastSentMethod(channel);
    setError(null);
    setSuccess(null);
    setIsSendingCode(true);
    try {
      const result = await sendMemberCode(memberToken, { channel });
      if (!result.success) {
        setError(t.failedToSendCode);
        return;
      }
      setSuccess(channel === "sms" ? t.codeSentSMS : t.codeSentEmail);
      setCodeSent(true);
    } catch (_err) {
      setError(t.failedToSendCode);
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleResendCode = async () => {
    if (!lastSentMethod) return;
    setError(null);
    setSuccess(null);
    setIsSendingCode(true);
    try {
      const result = await sendMemberCode(memberToken, { channel: lastSentMethod });
      if (!result.success) {
        setError(t.failedToSendCode);
        return;
      }
      setSuccess(lastSentMethod === "sms" ? t.codeSentSMS : t.codeSentEmail);
    } catch (_err) {
      setError(t.failedToSendCode);
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerify = (code: string) => {
    setError(null);
    startTransition(async () => {
      try {
        const result = await verifyMemberCode(memberToken, { code });
        // QL-505 — store exactly like the eID member flow does today
        // (`/p/session/page.tsx`'s member-scoped localStorage keys), then go
        // straight to the flow — no `?session=` round trip needed here since
        // we already hold the token.
        localStorage.setItem(MEMBER_SESSION_TOKEN_KEY, result.sessionToken);
        localStorage.setItem(MEMBER_SESSION_TOKEN_FOR_KEY, memberToken);
        navigate(result.redirectPath);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : null;
        // The server already translates the error (lockout included) into the flow language.
        setError(message || t.authFailed);
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!info || !info.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {unavailableReasonText(t, info?.unavailableReason ?? "not_found")}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { memberName, emailHint, phoneHint, bankIdAvailable, representativeName } = info;
  const heading = representativeName
    ? t.signInAsRepresentative(representativeName, memberName ?? "")
    : t.signInAs(memberName ?? "");
  const hasSignInMethod = !!emailHint || !!phoneHint || bankIdAvailable;

  if (errorCode === "identity_mismatch") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t.identityMismatch}</AlertDescription>
          </Alert>
          {bankIdAvailable && (
            <Button onClick={() => startCriiptoMemberLogin(memberToken)}>{t.tryAgain}</Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-primary rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">{heading}</CardTitle>
          {codeSent && <CardDescription>{t.enterCodeToContinue}</CardDescription>}
        </CardHeader>

        <CardContent>
          {!hasSignInMethod ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{t.noSignInMethod}</AlertDescription>
            </Alert>
          ) : !codeSent ? (
            <div className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="text-center space-y-2 py-2">
                <p className="text-sm text-muted-foreground">{t.chooseMethod}</p>
              </div>

              <div className="space-y-2">
                {emailHint && (
                  <Button
                    type="button"
                    onClick={() => sendCode("email")}
                    disabled={isSendingCode}
                    className="w-full gap-2"
                    variant={phoneHint ? "outline" : "default"}
                    loading={isSendingCode && lastSentMethod === "email"}
                  >
                    {t.sendCodeViaEmail} ({emailHint})
                  </Button>
                )}

                {phoneHint && (
                  <Button
                    type="button"
                    onClick={() => sendCode("sms")}
                    disabled={isSendingCode}
                    className="w-full gap-2"
                    variant={emailHint ? "outline" : "default"}
                    loading={isSendingCode && lastSentMethod === "sms"}
                  >
                    {t.sendCodeViaSMS} ({phoneHint})
                  </Button>
                )}
              </div>

              {bankIdAvailable && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        {t.orSignInWith}
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => startCriiptoMemberLogin(memberToken)}
                    className="w-full gap-2"
                  >
                    {t.bankIdMitId}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">{t.secureAuth}</p>
                </>
              )}
            </div>
          ) : (
            <CodeEntryForm
              channel={lastSentMethod ?? "email"}
              onVerify={handleVerify}
              onResend={handleResendCode}
              verifying={isPending}
              resending={isSendingCode}
              error={error}
              success={success}
              language={info?.language ?? userLanguage}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
